/**
 * Canvas压缩器 - 基于绘制的兼容性视频压缩
 * 
 * 特点：
 * ✅ 浏览器兼容性好
 * ✅ 可以精确控制每一帧
 * ✅ 支持分辨率调整
 * ✅ 无需额外库
 * ❌ 需要完整播放时间
 * ❌ 重新编码，质量损失
 * ❌ CPU密集型操作
 */

interface CanvasCompressOptions {
  width?: number;
  height?: number;
  fps?: number;
  quality?: number;      // 0-1
  bitrate?: number;
  mimeType?: string;
  enableProgressLog?: boolean;
}

interface CompressResult {
  success: boolean;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  blob: Blob;
  processingTime: number;
  method: string;
  error?: string;
}

export class CanvasCompressor {
  
  /**
   * Canvas绘制压缩
   */
  static async compress(
    file: File,
    options: CanvasCompressOptions = {}
  ): Promise<CompressResult> {
    const startTime = Date.now();

    const {
      width,
      height,
      fps = 30,
      quality = 0.8,
      bitrate,
      mimeType,
      enableProgressLog = true
    } = options;

    try {
      if (enableProgressLog) {
        console.log('🎨 Canvas压缩开始:', {
          原始大小: `${(file.size / (1024 * 1024)).toFixed(2)}MB`,
          目标分辨率: width && height ? `${width}x${height}` : '自动',
          帧率: `${fps}fps`,
          质量: `${(quality * 100).toFixed(0)}%`,
          比特率: bitrate ? `${(bitrate / 1000).toFixed(0)}kbps` : '自动'
        });
      }

      // 加载视频
      const video = await this.loadVideo(file);
      
      // 计算目标尺寸
      const targetSize = this.calculateTargetSize(video, width, height);
      
      // 创建canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('Canvas上下文不可用');
      }

      canvas.width = targetSize.width;
      canvas.height = targetSize.height;

      // 选择最佳MIME类型
      const selectedMimeType = mimeType || this.selectBestMimeType();
      
      // 计算比特率
      const targetBitrate = bitrate || this.calculateBitrate(file.size, video.duration);

      if (enableProgressLog) {
        console.log('🎬 开始Canvas绘制压缩:', {
          原始尺寸: `${video.videoWidth}x${video.videoHeight}`,
          目标尺寸: `${canvas.width}x${canvas.height}`,
          时长: `${video.duration.toFixed(1)}秒`,
          编码格式: selectedMimeType,
          比特率: `${(targetBitrate / 1000).toFixed(0)}kbps`
        });
      }

      // 开始录制
      const compressedBlob = await this.recordCanvas(
        canvas, 
        ctx, 
        video, 
        { fps, targetBitrate, selectedMimeType, enableProgressLog }
      );

      const processingTime = Date.now() - startTime;

      const result: CompressResult = {
        success: true,
        originalSize: file.size,
        compressedSize: compressedBlob.size,
        compressionRatio: compressedBlob.size / file.size,
        blob: compressedBlob,
        processingTime,
        method: 'Canvas绘制压缩'
      };

      if (enableProgressLog) {
        console.log('✅ Canvas压缩完成:', {
          原始大小: `${(file.size / (1024 * 1024)).toFixed(2)}MB`,
          压缩后大小: `${(compressedBlob.size / (1024 * 1024)).toFixed(2)}MB`,
          压缩率: `${((1 - result.compressionRatio) * 100).toFixed(1)}%`,
          处理时间: `${(processingTime / 1000).toFixed(1)}秒`
        });
      }

      return result;

    } catch (error) {
      const processingTime = Date.now() - startTime;
      console.error('❌ Canvas压缩失败:', error);
      
      return {
        success: false,
        originalSize: file.size,
        compressedSize: file.size,
        compressionRatio: 1,
        blob: new Blob([file], { type: file.type }),
        processingTime,
        method: 'Canvas绘制压缩',
        error: error instanceof Error ? error.message : '未知错误'
      };
    }
  }

  /**
   * 加载视频文件
   */
  private static loadVideo(file: File): Promise<HTMLVideoElement> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.src = URL.createObjectURL(file);
      video.muted = true;
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        resolve(video);
      };
      
      video.onerror = () => {
        reject(new Error('视频加载失败'));
      };
      
      setTimeout(() => reject(new Error('视频加载超时')), 10000);
    });
  }

  /**
   * 计算目标尺寸
   */
  private static calculateTargetSize(
    video: HTMLVideoElement, 
    targetWidth?: number, 
    targetHeight?: number
  ): { width: number; height: number } {
    const { videoWidth: originalWidth, videoHeight: originalHeight } = video;
    
    if (targetWidth && targetHeight) {
      return { width: targetWidth, height: targetHeight };
    }
    
    if (targetWidth) {
      const aspectRatio = originalHeight / originalWidth;
      return { 
        width: targetWidth, 
        height: Math.round(targetWidth * aspectRatio) 
      };
    }
    
    if (targetHeight) {
      const aspectRatio = originalWidth / originalHeight;
      return { 
        width: Math.round(targetHeight * aspectRatio), 
        height: targetHeight 
      };
    }
    
    // 智能缩放 - 保持合理尺寸
    const maxSize = 1280;
    if (originalWidth > maxSize || originalHeight > maxSize) {
      const scale = maxSize / Math.max(originalWidth, originalHeight);
      return {
        width: Math.round(originalWidth * scale),
        height: Math.round(originalHeight * scale)
      };
    }
    
    return { width: originalWidth, height: originalHeight };
  }

  /**
   * 选择最佳MIME类型
   */
  private static selectBestMimeType(): string {
    const types = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/mp4;codecs=h264',
      'video/webm'
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return 'video/webm';
  }

  /**
   * 计算合适的比特率
   */
  private static calculateBitrate(fileSize: number, duration: number): number {
    const fileSizeMB = fileSize / (1024 * 1024);
    
    // 根据文件大小智能计算
    if (fileSizeMB <= 50) {
      return 1500000; // 1.5Mbps
    } else if (fileSizeMB <= 100) {
      return 1000000; // 1Mbps
    } else if (fileSizeMB <= 200) {
      return 800000;  // 0.8Mbps
    } else {
      return 600000;  // 0.6Mbps
    }
  }

  /**
   * 录制Canvas内容
   */
  private static recordCanvas(
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    video: HTMLVideoElement,
    options: {
      fps: number;
      targetBitrate: number;
      selectedMimeType: string;
      enableProgressLog: boolean;
    }
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const { fps, targetBitrate, selectedMimeType, enableProgressLog } = options;
      
      // 创建录制器
      const stream = canvas.captureStream(fps);
      const recorder = new MediaRecorder(stream, {
        mimeType: selectedMimeType,
        videoBitsPerSecond: targetBitrate
      });

      const chunks: Blob[] = [];
      let frameCount = 0;
      const frameInterval = 1000 / fps;
      let lastFrameTime = 0;
      let progressTimer: NodeJS.Timeout | null = null;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        if (progressTimer) {
          clearInterval(progressTimer);
        }
        const blob = new Blob(chunks, { type: selectedMimeType });
        resolve(blob);
      };

      recorder.onerror = (event) => {
        if (progressTimer) {
          clearInterval(progressTimer);
        }
        reject(new Error('录制失败'));
      };

      // 开始录制
      recorder.start();
      
      // 播放视频
      video.currentTime = 0;
      video.playbackRate = 1.0;
      video.play();

      // 进度监控
      if (enableProgressLog) {
        progressTimer = setInterval(() => {
          if (video.duration) {
            const progress = (video.currentTime / video.duration * 100).toFixed(1);
            console.log(`⏳ Canvas压缩进度: ${progress}%`);
          }
        }, 5000);
      }

      // 绘制帧
      const drawFrame = () => {
        if (video.ended || video.paused) {
          console.log(`🏁 Canvas绘制完成，总帧数: ${frameCount}`);
          setTimeout(() => {
            if (recorder.state === 'recording') {
              recorder.stop();
            }
          }, 300);
          return;
        }

        const currentTime = performance.now();
        
        if (currentTime - lastFrameTime >= frameInterval) {
          // 绘制当前帧
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          frameCount++;
          lastFrameTime = currentTime;
        }
        
        requestAnimationFrame(drawFrame);
      };

      // 开始绘制
      video.onplay = () => {
        if (enableProgressLog) {
          console.log('▶️ 开始Canvas绘制');
        }
        drawFrame();
      };

      video.onended = () => {
        if (enableProgressLog) {
          console.log('📹 视频播放结束');
        }
      };

      // 超时保护
      setTimeout(() => {
        if (recorder.state === 'recording') {
          console.warn('⚠️ Canvas压缩超时，强制停止');
          recorder.stop();
        }
      }, 300000); // 5分钟超时
    });
  }

  /**
   * 智能压缩预设
   */
  static async compressWithPreset(
    file: File,
    preset: 'fast' | 'balanced' | 'quality' | 'small_size'
  ): Promise<CompressResult> {
    let options: CanvasCompressOptions;

    switch (preset) {
      case 'fast':
        options = {
          fps: 24,
          quality: 0.7,
          width: 854,
          height: 480
        };
        break;
      
      case 'balanced':
        options = {
          fps: 30,
          quality: 0.8,
          width: 1280,
          height: 720
        };
        break;
      
      case 'quality':
        options = {
          fps: 30,
          quality: 0.9
        };
        break;
      
      case 'small_size':
        options = {
          fps: 20,
          quality: 0.6,
          width: 640,
          height: 360
        };
        break;
      
      default:
        options = { fps: 30, quality: 0.8 };
    }

    console.log(`🎯 使用${preset}预设进行Canvas压缩`);
    return this.compress(file, options);
  }

  /**
   * 批量压缩
   */
  static async compressBatch(
    files: File[],
    options: CanvasCompressOptions = {}
  ): Promise<CompressResult[]> {
    const results: CompressResult[] = [];
    
    console.log(`🚀 开始批量Canvas压缩 ${files.length} 个文件`);
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      console.log(`⏳ 处理第 ${i + 1}/${files.length} 个文件: ${file.name}`);
      
      const result = await this.compress(file, {
        ...options,
        enableProgressLog: false
      });
      
      results.push(result);
      
      console.log(`${result.success ? '✅' : '❌'} 第 ${i + 1} 个文件处理完成`);
    }
    
    console.log('🎉 批量压缩完成');
    return results;
  }
} 